import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';

/**
 * Cheap duplicate detector — runs a debounced lookup against the
 * existing list endpoints and surfaces a "este ya existe" banner so
 * the user can open the existing record instead of double-creating.
 *
 * Non-blocking: it's a warning, not a hard stop. The server still
 * enforces the unique-name index on accounts and the unique
 * (account_id, email) constraint on contacts.
 */

interface AccountMatch {
  id: string;
  name: string;
  industry?: string | null;
  country?: string | null;
}

interface ContactMatch {
  id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
}

const DEBOUNCE_MS = 350;

export function AccountDuplicateBanner({
  name,
  taxId,
  onOpen,
}: {
  name: string;
  taxId?: string | null;
  onOpen: (accountId: string) => void;
}) {
  const { t } = useTranslation();
  const [matches, setMatches] = useState<AccountMatch[]>([]);

  useEffect(() => {
    const q = name.trim();
    if (q.length < 3 && !taxId) {
      setMatches([]);
      return;
    }
    const handle = setTimeout(() => {
      // Use the existing search param — we filter further client-side
      // by exact-match on tax_id when present.
      const params = q ? `?search=${encodeURIComponent(q)}` : '';
      apiClient
        .get<AccountMatch[]>(`/accounts${params}`)
        .then(({ data }) => {
          const filtered = (data ?? []).filter((a) =>
            a.name.toLowerCase() === q.toLowerCase(),
          );
          setMatches(filtered.slice(0, 3));
        })
        .catch(() => setMatches([]));
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [name, taxId]);

  if (matches.length === 0) return null;

  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-700 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="flex-1">
        <p className="font-medium">{t('forms.duplicate.account.title')}</p>
        <ul className="mt-1 space-y-1">
          {matches.map((m) => (
            <li key={m.id} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onOpen(m.id)}
                className="inline-flex items-center gap-1 underline hover:text-amber-50"
              >
                {m.name}
                <ExternalLink className="h-3 w-3" aria-hidden />
              </button>
              {(m.industry || m.country) && (
                <span className="text-xs opacity-75">
                  {[m.industry, m.country].filter(Boolean).join(' · ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function ContactDuplicateBanner({
  email,
  accountId,
  onOpen,
}: {
  email: string;
  accountId: string | null;
  onOpen: (contactId: string) => void;
}) {
  const { t } = useTranslation();
  const [matches, setMatches] = useState<ContactMatch[]>([]);

  useEffect(() => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes('@')) {
      setMatches([]);
      return;
    }
    const handle = setTimeout(() => {
      const url = accountId
        ? `/accounts/${accountId}/contacts?search=${encodeURIComponent(e)}`
        : `/contacts?search=${encodeURIComponent(e)}`;
      apiClient
        .get<ContactMatch[]>(url)
        .then(({ data }) => {
          const filtered = (data ?? []).filter(
            (c) => (c.email ?? '').toLowerCase() === e,
          );
          setMatches(filtered.slice(0, 3));
        })
        .catch(() => setMatches([]));
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [email, accountId]);

  if (matches.length === 0) return null;

  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-700 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="flex-1">
        <p className="font-medium">{t('forms.duplicate.contact.title')}</p>
        <ul className="mt-1 space-y-1">
          {matches.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                onClick={() => onOpen(m.id)}
                className="inline-flex items-center gap-1 underline hover:text-amber-50"
              >
                {m.firstName} {m.lastName} ({m.email})
                <ExternalLink className="h-3 w-3" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
